import { useEffect } from 'react';

import { Box, Flex } from '@chakra-ui/react';
import { useDispatch } from 'react-redux';

import { Delete, Plus } from 'assets/assetsV1/iconComponents';
import { Level } from 'assets/assetsV1/iconComponents/Level';

import { roundOffToPlaces } from 'constants/helper';

import { Button } from 'uiToolkitV1/Button';
import { InputV2 } from 'uiToolkitV1/InputV2';
import { NumberInput } from 'uiToolkitV1/InputV2/NumberInput';
import { SkeletonLoader } from 'uiToolkitV1/SkeletonLoader';
import { TextBodyMedium, TextLabel } from 'uiToolkitV1/Typography';

import { setLabelSettings, useCompetencySettings } from '../competencySettingsSlice';
import { LabelOption } from '../types/competencySettingsSlice';
import { Container } from './components/Container';
import { useGetCompetencyLabelsQuery } from './dataHooks';
import { isScorePercentValidator } from './helpers/isScorePercentValidator';

const LevelIcon = (props) => (
  <Box width="24px">
    <Level {...props} boxSize="18px" />
  </Box>
);

export const CompetencyLevels = () => {
  const { labelSettings } = useCompetencySettings();
  const { data: competencyLabels, isLoading } = useGetCompetencyLabelsQuery();
  const dispatch = useDispatch();

  const validationError = isScorePercentValidator(labelSettings);

  useEffect(() => {
    if (competencyLabels && !labelSettings.length) {
      dispatch(
        setLabelSettings(
          competencyLabels.answerOptions.map((option) => ({ upperLimit: option.score, label: option.description }))
        )
      );
    }
  }, [competencyLabels, dispatch, labelSettings]);

  const handleChange = (value: number | string, index: number, key: keyof LabelOption) => {
    const newLabelSettings = labelSettings.map((setting, i) => {
      if (i === index) {
        return { ...setting, [key]: value };
      }
      return setting;
    });
    dispatch(setLabelSettings(newLabelSettings));
  };

  const handleDelete = (index: number) => {
    const newLabelSettings = labelSettings.filter((_, i) => i !== index);
    dispatch(setLabelSettings(newLabelSettings));
  };

  const handleAdd = () => {
    const lastScore = labelSettings[labelSettings.length - 1].upperLimit;
    const newLabelSettings = [...labelSettings, { upperLimit: lastScore + 1, label: '' }];
    dispatch(setLabelSettings(newLabelSettings));
  };

  if (isLoading) {
    return (
      <Container
        title="Competency Levels"
        description="Customize competency level names and scales (3-10 levels) to align with your organization's definitions for personalized employee evaluations"
        icon={LevelIcon}
      >
        <SkeletonLoader />
      </Container>
    );
  }

  return (
    <Container
      title="Competency Levels"
      description="Customize competency level names and scales (3-10 levels) to align with your organization's definitions for personalized employee evaluations"
      icon={LevelIcon}
    >
      <Box maxH="calc(100vh - 400px)" overflow="auto">
        {labelSettings.map((setting, i) => {
          const start = i - 1 >= 0 ? roundOffToPlaces(Number(labelSettings[i - 1].upperLimit), 2) : 0;

          return (
            // eslint-disable-next-line react/no-array-index-key
            <Flex key={i} flexDir="column">
              <Flex borderBottom="1px solid" borderColor="neutral.EBEFF3" p="12px 6px 24px" alignItems="center">
                {i > 2 ? (
                  <Box cursor="pointer" tabIndex={0} onClick={() => handleDelete(i)}>
                    <Delete color="text.g2" boxSize="24px" />
                  </Box>
                ) : (
                  <Box width="24px" />
                )}
                <Box minW="82px" ml={4}>
                  <TextBodyMedium>{i === 0 ? 'Equal to' : 'Greater than'}</TextBodyMedium>
                </Box>

                <Box ml={4}>
                  <NumberInput width={i === 0 ? '301px' : '120px'} value={start} isDisabled />
                </Box>
                {i !== 0 && (
                  <>
                    <TextBodyMedium ml={4}>upto</TextBodyMedium>
                    <NumberInput
                      width="120px"
                      ml={4}
                      value={setting.upperLimit}
                      onChange={(valueAsString) => handleChange(valueAsString, i, 'upperLimit')}
                      onBlur={(e) => handleChange(roundOffToPlaces(Number(e.target.value), 1), i, 'upperLimit')}
                      min={0}
                      max={10000}
                      isInvalid={validationError?.index === i && validationError.reason === 'score'}
                    />
                  </>
                )}
                <TextBodyMedium ml={4} mr={4}>
                  is
                </TextBodyMedium>
                <Box width="200px">
                  <InputV2
                    value={setting.label}
                    onChange={(e) => {
                      handleChange(e.target.value, i, 'label');
                    }}
                    isDashed
                    isInvalid={validationError?.index === i && validationError.reason === 'label'}
                  />
                </Box>
              </Flex>
              {validationError?.index === i && (
                <TextLabel color="alerts.negative" m="12px 8px 20px">
                  {validationError.message}
                </TextLabel>
              )}
            </Flex>
          );
        })}
      </Box>
      {labelSettings.length < 10 ? (
        <Button alignSelf="start" mt={3} variant="tertiary" StartIcon={Plus} onClick={handleAdd}>
          Add Competency Level
        </Button>
      ) : (
        <Box h="40px" mt={3} />
      )}
    </Container>
  );
};
